import React from 'react';
import Icon from '../../../components/AppIcon';

const CampaignEffectivenessPanel = ({ campaignData, dateRange }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="Zap" className="w-5 h-5 text-blue-600" />
          Campaign Effectiveness Analysis
        </h2>
      </div>

      {/* Active Campaigns */}
      <div className="space-y-4 mb-6">
        {campaignData?.map((campaign) => (
          <div key={campaign?.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-gray-900">{campaign?.name}</h3>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  campaign?.status === 'active' ? 'bg-green-100 text-green-800' :
                  campaign?.status === 'completed'? 'bg-gray-100 text-gray-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {campaign?.status}
                </span>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">ROI</p>
                <p className="text-lg font-bold text-green-600">{campaign?.roi}%</p>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Leads Generated</p>
                <p className="text-lg font-semibold text-gray-900">{campaign?.leads}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Conversions</p>
                <p className="text-lg font-semibold text-gray-900">{campaign?.conversions}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Total Spend</p>
                <p className="text-lg font-semibold text-gray-900">K{campaign?.spend?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Conversion Rate</p>
                <p className="text-lg font-semibold text-gray-900">
                  {((campaign?.conversions / campaign?.leads) * 100)?.toFixed(1)}%
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>Start: {new Date(campaign?.startDate)?.toLocaleDateString()}</span>
              <span>End: {new Date(campaign?.endDate)?.toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>

      {/* A/B Testing Results */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">A/B Testing Results</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">Variant A: Traditional Messaging</h4>
              <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">Control</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Click-through rate:</span>
                <span className="font-semibold">3.2%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Conversion rate:</span>
                <span className="font-semibold">15.8%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cost per conversion:</span>
                <span className="font-semibold">K285</span>
              </div>
            </div>
          </div>
          <div className="border-2 border-green-500 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">Variant B: PNG-Focused Messaging</h4>
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">Winner</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Click-through rate:</span>
                <span className="font-semibold text-green-600">4.7% (+46.9%)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Conversion rate:</span>
                <span className="font-semibold text-green-600">21.3% (+34.8%)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cost per conversion:</span>
                <span className="font-semibold text-green-600">K198 (-30.5%)</span>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-3 bg-green-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm text-green-800">
            <Icon name="CheckCircle" className="w-4 h-4" />
            <span className="font-medium">Statistical significance: 95% confidence level</span>
          </div>
        </div>
      </div>

      {/* Message Optimization Insights */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
          <Icon name="Lightbulb" className="w-4 h-4 text-blue-600" />
          Message Optimization Insights
        </h3>
        <ul className="text-sm text-gray-700 space-y-2">
          <li className="flex items-start gap-2">
            <Icon name="Check" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span>PNG-specific compliance messaging increases engagement by 47%</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span>Government department targeting shows 35% higher conversion rates</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span>ROI calculator inclusion in landing pages improves lead quality by 28%</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span>Video testimonials from PNG clients increase trust signals by 42%</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CampaignEffectivenessPanel;